import {
  TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY,
  TASK_TYPE_INPUT_FIELDS_CONFIG_KEY,
} from '@erp/workflows';

export type SelectedTaskTypeInputField = {
  fieldName: string;
  fieldType: string;
  label: string;
  isRequired: boolean;
  isMultiple: boolean;
  referencedEntityType?: string | null;
};

export type SelectedTaskTypeForStepConfig = {
  key: string;
  anchorEntityType?: string | null;
  taskNamespace?: unknown;
  inputFields?: readonly SelectedTaskTypeInputField[] | null;
};

/** Map a task-type catalog row onto the picker/hydration shape. */
export function selectedTaskTypeForStepConfig(taskType: {
  key: string;
  anchorEntityType?: string | null;
  taskNamespace?: unknown;
  inputSchema?: { fields: readonly SelectedTaskTypeInputField[] } | null;
}): SelectedTaskTypeForStepConfig {
  return {
    key: taskType.key,
    anchorEntityType: taskType.anchorEntityType,
    taskNamespace: taskType.taskNamespace,
    inputFields: taskType.inputSchema?.fields ?? null,
  };
}

/**
 * Next step config after the Task Type picker changes. Copies the type's
 * declared `anchorEntityType` onto `taskTypeAnchorEntityType` so
 * create-family input sockets can regenerate without a server lookup, and
 * drops a node-level namespace when the type already owns one.
 */
export function nextConfigForTaskTypeSelection(
  config: Record<string, unknown>,
  nextType: SelectedTaskTypeForStepConfig | null,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...config };
  delete next.taskTypeKey;
  delete next[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY];
  delete next[TASK_TYPE_INPUT_FIELDS_CONFIG_KEY];

  if (nextType === null) {
    return next;
  }

  if (nextType.taskNamespace) {
    delete next.taskNamespaceKey;
    delete next.taskNamespaceId;
  }

  next.taskTypeKey = nextType.key;
  if (nextType.anchorEntityType) {
    next[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY] = nextType.anchorEntityType;
  }
  if (nextType.inputFields && nextType.inputFields.length > 0) {
    next[TASK_TYPE_INPUT_FIELDS_CONFIG_KEY] = nextType.inputFields.map(
      (field) => ({
        fieldName: field.fieldName,
        fieldType: field.fieldType,
        label: field.label,
        isRequired: field.isRequired,
        isMultiple: field.isMultiple,
        ...(field.referencedEntityType
          ? { referencedEntityType: field.referencedEntityType }
          : {}),
      }),
    );
  }
  return next;
}

/**
 * True when the loaded type's declared anchor is not yet copied onto the
 * node config (saved before this key existed, or the type later gained/lost
 * an anchor).
 */
export function taskTypeAnchorConfigNeedsSync(
  config: Record<string, unknown>,
  selectedType: SelectedTaskTypeForStepConfig | undefined,
): boolean {
  if (!selectedType) return false;
  const next = nextConfigForTaskTypeSelection(config, selectedType);
  const currentAnchor =
    typeof config[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY] === 'string'
      ? config[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY]
      : undefined;
  const nextAnchor =
    typeof next[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY] === 'string'
      ? next[TASK_TYPE_ANCHOR_ENTITY_TYPE_CONFIG_KEY]
      : undefined;
  return (
    currentAnchor !== nextAnchor ||
    JSON.stringify(config[TASK_TYPE_INPUT_FIELDS_CONFIG_KEY] ?? null) !==
      JSON.stringify(next[TASK_TYPE_INPUT_FIELDS_CONFIG_KEY] ?? null)
  );
}

/**
 * Config to persist once the org catalog has loaded for `config.taskTypeKey`.
 * `null` when there is nothing to write (no type, or already in sync).
 */
export function syncedConfigForLoadedTaskType(
  config: Record<string, unknown>,
  selectedType: SelectedTaskTypeForStepConfig | undefined,
): Record<string, unknown> | null {
  if (!selectedType || !taskTypeAnchorConfigNeedsSync(config, selectedType)) {
    return null;
  }
  return nextConfigForTaskTypeSelection(config, selectedType);
}

/**
 * Whether the namespace selector should stay locked. Task-type loading and
 * errors are included: until the selected type is known, a namespace-owning
 * type would look unset and an edit could persist a stale override.
 */
export function taskNamespaceSelectAvailability(opts: {
  canReadTasks: boolean;
  namespacesError: boolean;
  namespacesLoading: boolean;
  taskTypesError: boolean;
  taskTypesLoading: boolean;
}): {
  namespacesUnavailable: boolean;
  loading: boolean;
} {
  return {
    namespacesUnavailable:
      !opts.canReadTasks || opts.namespacesError || opts.taskTypesError,
    loading: opts.namespacesLoading || opts.taskTypesLoading,
  };
}
